// Token storage utility for managing access tokens, refresh tokens, and user data
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_DATA_KEY = 'user_data';

/** Fired after login/logout so the shell re-reads the stored user. */
export const AUTH_USER_CHANGED_EVENT = 'larkpm:auth-user-changed';

function notifyAuthUserChanged(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(AUTH_USER_CHANGED_EVENT));
}

export const tokenStorage = {
    // Get access token from localStorage
    getToken: (): string | null => {
        try {
            return localStorage.getItem(TOKEN_KEY);
        } catch (error) {
            console.error('Error getting token from localStorage:', error);
            return null;
        }
    },

    // Store access token in localStorage
    setToken: (token: string): void => {
        try {
            localStorage.setItem(TOKEN_KEY, token);
        } catch (error) {
            console.error('Error storing token in localStorage:', error);
        }
    },

    // Remove access token from localStorage
    removeToken: (): void => {
        try {
            localStorage.removeItem(TOKEN_KEY);
        } catch (error) {
            console.error('Error removing token from localStorage:', error);
        }
    },

    // Get refresh token from localStorage
    getRefreshToken: (): string | null => {
        try {
            return localStorage.getItem(REFRESH_TOKEN_KEY);
        } catch (error) {
            console.error('Error getting refresh token from localStorage:', error);
            return null;
        }
    },

    // Store refresh token in localStorage
    setRefreshToken: (token: string): void => {
        try {
            localStorage.setItem(REFRESH_TOKEN_KEY, token);
        } catch (error) {
            console.error('Error storing refresh token in localStorage:', error);
        }
    },

    // Remove refresh token from localStorage
    removeRefreshToken: (): void => {
        try {
            localStorage.removeItem(REFRESH_TOKEN_KEY);
        } catch (error) {
            console.error('Error removing refresh token from localStorage:', error);
        }
    },

    // Store both access and refresh tokens
    setTokens: (accessToken: string, refreshToken: string): void => {
        tokenStorage.setToken(accessToken);
        tokenStorage.setRefreshToken(refreshToken);
    },

    // Check if token exists
    hasToken: (): boolean => {
        return !!tokenStorage.getToken();
    },

    // Check if refresh token exists
    hasRefreshToken: (): boolean => {
        return !!tokenStorage.getRefreshToken();
    },

    // Store user data in localStorage
    setUserData: (userData: any): void => {
        try {
            localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
            notifyAuthUserChanged();
        } catch (error) {
            console.error('Error storing user data in localStorage:', error);
        }
    },

    // Get user data from localStorage
    getUserData: (): any | null => {
        try {
            const userData = localStorage.getItem(USER_DATA_KEY);
            return userData ? JSON.parse(userData) : null;
        } catch (error) {
            console.error('Error getting user data from localStorage:', error);
            return null;
        }
    },

    // Remove user data from localStorage
    removeUserData: (): void => {
        try {
            localStorage.removeItem(USER_DATA_KEY);
        } catch (error) {
            console.error('Error removing user data from localStorage:', error);
        }
    },

    // Clear all stored data
    clearAll: (): void => {
        tokenStorage.removeToken();
        tokenStorage.removeRefreshToken();
        tokenStorage.removeUserData();
        notifyAuthUserChanged();
    }
};
